import { AlertTriangle, Receipt } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { updateInvoicingSettings } from '@/data/settingsRepo';
import { useInvoicingSettings } from '@/hooks/useInvoicingSettings';
import type { InvoicingSettingsType } from '@/types/types_settings';
import { formatDate } from '@/util/util_format';

/**
 * What every monthly invoice prints about us: the issuer block, the bank
 * account the partner pays into, and the standing wording at the foot.
 *
 * Stored rather than compiled in because none of it is engineering's to decide
 * and all of it changes without a release — a new registration number, a moved
 * office, a different account. The invoice renderer reads this and nothing else
 * about Eighty Days.
 *
 * Lives on Settings rather than beside the invoices themselves, because it is
 * not about any one invoice. It is the letterhead.
 */
export function InvoicingSettingsCard() {
  const { settings, loading, missing } = useInvoicingSettings();

  return (
    <section className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
      <div className='tw:flex tw:items-center tw:gap-3'>
        <Receipt className='tw:size-5 tw:text-primary' />
        <div>
          <p className='tw:font-medium'>Invoicing</p>
          <p className='tw:text-sm tw:text-muted-foreground'>
            Printed on every monthly invoice. Changes apply to invoices issued from now on, never to
            one already sent.
          </p>
        </div>
      </div>

      {missing.length > 0 && !loading && (
        <p className='tw:mt-5 tw:flex tw:items-start tw:gap-2 tw:rounded-md tw:border tw:border-ed-red/40 tw:bg-ed-red/5 tw:px-4 tw:py-3 tw:text-sm'>
          <AlertTriangle className='tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-ed-red' />
          <span>
            Invoices cannot be issued until these are filled in: {missing.join(', ')}. A draft can
            still be generated and read.
          </span>
        </p>
      )}

      {loading
        ? <p className='tw:mt-6 tw:text-sm tw:text-muted-foreground'>Loading…</p>
        // Mounted only once the stored document is in, so the form's fields
        // initialise from it and are then the typist's alone. The subscription
        // deliberately does not flow into them afterwards: a remote change
        // overwriting the field under the cursor is how a half-typed account
        // number gets lost.
        : <InvoicingSettingsForm initial={settings} />}
    </section>
  );
}

function InvoicingSettingsForm({ initial }: { initial: InvoicingSettingsType }) {
  const { user } = useAuth();
  const [draft, setDraft] = useState<InvoicingSettingsType>(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof InvoicingSettingsType>(key: K, value: InvoicingSettingsType[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await updateInvoicingSettings(draft, user?.uid);
      setSaved(true);
    } catch (e) {
      console.error('Failed to save invoicing settings', e);
      setError('Could not save that. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className='tw:mt-6 tw:grid tw:gap-6 tw:md:grid-cols-2'>
        <fieldset className='tw:space-y-4'>
          <legend className='tw:text-xs tw:font-medium tw:tracking-wider tw:uppercase tw:text-muted-foreground'>
            Issuer
          </legend>
          <Field label='Company name' value={draft.companyName} onChange={(v) => set('companyName', v)} />
          <Field label='Postal code' value={draft.postalCode} onChange={(v) => set('postalCode', v)} />
          <div className='tw:space-y-2'>
            <Label htmlFor='inv-address'>Address</Label>
            <Textarea
              id='inv-address'
              rows={2}
              value={draft.address}
              onChange={(e) => set('address', e.target.value)}
            />
            <p className='tw:text-xs tw:text-muted-foreground'>One line per line, as it should print.</p>
          </div>
          <Field label='Telephone' value={draft.phone} onChange={(v) => set('phone', v)} />
          <Field
            label='登録番号'
            value={draft.registrationNumber}
            onChange={(v) => set('registrationNumber', v)}
            hint='The qualified-invoice registration number, T followed by 13 digits. Omitted from the document entirely when blank.'
          />
        </fieldset>

        <fieldset className='tw:space-y-4'>
          <legend className='tw:text-xs tw:font-medium tw:tracking-wider tw:uppercase tw:text-muted-foreground'>
            Bank
          </legend>
          <Field label='Bank' value={draft.bankName} onChange={(v) => set('bankName', v)} />
          <Field label='Branch' value={draft.bankBranch} onChange={(v) => set('bankBranch', v)} />
          <Field label='Account type' value={draft.accountType} onChange={(v) => set('accountType', v)} />
          <Field label='Account number' value={draft.accountNumber} onChange={(v) => set('accountNumber', v)} />
          <Field
            label='Account holder'
            value={draft.accountHolder}
            onChange={(v) => set('accountHolder', v)}
            hint='In katakana, as the bank holds it.'
          />
        </fieldset>
      </div>

      <div className='tw:mt-6 tw:space-y-4'>
        <div className='tw:space-y-2'>
          <Label htmlFor='inv-remarks'>備考</Label>
          <Textarea
            id='inv-remarks'
            rows={2}
            value={draft.remarks}
            onChange={(e) => set('remarks', e.target.value)}
          />
        </div>

        <Field
          label='角印 (seal) file'
          value={draft.hankoPath}
          onChange={(v) => set('hankoPath', v)}
          hint={'Storage path of the company seal, e.g. internal/hanko.png. Upload it in the Firebase console '
            + '— it is deliberately not uploadable from here, and nothing in a browser can read it back. '
            + 'Leave blank to issue unsealed invoices; a path that is set but unreadable fails the render '
            + 'rather than sending a month of unsealed ones.'}
        />

        <div className='tw:max-w-40 tw:space-y-2'>
          <Label htmlFor='inv-tax'>Default 税率 (%)</Label>
          <Input
            id='inv-tax'
            type='number'
            min={0}
            max={100}
            value={draft.defaultTaxRate}
            onChange={(e) => set('defaultTaxRate', Number(e.target.value))}
          />
        </div>
      </div>

      <div className='tw:mt-6 tw:flex tw:flex-wrap tw:items-center tw:gap-4'>
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save invoicing details'}
        </Button>
        {saved && <span className='tw:text-sm tw:text-muted-foreground'>Saved.</span>}
        {initial.updatedAt && !saved && (
          <span className='tw:text-xs tw:text-muted-foreground'>
            Last changed {formatDate(initial.updatedAt)}.
          </span>
        )}
      </div>

      {error && <p className='tw:mt-3 tw:text-sm tw:text-ed-red'>{error}</p>}
    </>
  );
}

/** A labelled text input. Local to this card; there is no shared form kit yet. */
function Field(
  { label, value, onChange, hint }: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    hint?: string;
  },
) {
  const id = `inv-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className='tw:space-y-2'>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
      {hint && <p className='tw:text-xs tw:text-muted-foreground'>{hint}</p>}
    </div>
  );
}
